import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GameViewProps } from '../../core/contracts'
import { haptics } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import { describeAnswer } from './logic'
import type { MatrixAnswer, MatrixRound } from './logic'
import './view.css'

/**
 * Экран Memory Matrix.
 *
 * Три фазы: показ паттерна -> ввод -> обратная связь.
 * Логика игры здесь не живёт, только отображение и сбор ввода.
 */

type Phase = 'show' | 'input' | 'feedback'

const FEEDBACK_MS = 1100

export function MemoryMatrixView({ round, level, onAnswer }: GameViewProps<MatrixRound, MatrixAnswer>) {
  const { t } = useT()
  const [phase, setPhase] = useState<Phase>('show')
  const [picked, setPicked] = useState<ReadonlySet<number>>(new Set())
  const inputStartedAt = useRef(0)
  const answerRef = useRef<MatrixAnswer | null>(null)

  const total = round.gridSize * round.gridSize
  const cells = useMemo(() => Array.from({ length: total }, (_, i) => i), [total])
  const patternSet = useMemo(() => new Set(round.pattern), [round.pattern])

  // фаза показа: гасим паттерн по таймеру и открываем ввод
  useEffect(() => {
    const id = setTimeout(() => {
      inputStartedAt.current = performance.now()
      setPhase('input')
    }, round.showMs)
    return () => clearTimeout(id)
  }, [round.showMs])

  const toggle = useCallback(
    (cell: number) => {
      if (phase !== 'input') return
      haptics.cellPress()
      setPicked((prev) => {
        const next = new Set(prev)
        if (next.has(cell)) next.delete(cell)
        else next.add(cell)
        return next
      })
    },
    [phase],
  )

  const confirm = useCallback(() => {
    if (phase !== 'input') return
    const answer: MatrixAnswer = {
      cells: [...picked],
      rtMs: performance.now() - inputStartedAt.current,
    }
    answerRef.current = answer
    setPhase('feedback')
  }, [phase, picked])

  // фаза обратной связи: показали разбор - и передали ответ движку
  useEffect(() => {
    if (phase !== 'feedback') return
    const id = setTimeout(() => {
      const answer = answerRef.current
      if (answer) onAnswer(answer)
    }, FEEDBACK_MS)
    return () => clearTimeout(id)
  }, [phase, onAnswer])

  const review = useMemo(() => {
    if (phase !== 'feedback' || !answerRef.current) return null
    return describeAnswer(round, answerRef.current)
  }, [phase, round])

  const cellClass = (cell: number): string => {
    if (phase === 'show') {
      return patternSet.has(cell) ? 'mm__cell mm__cell--lit' : 'mm__cell'
    }
    if (phase === 'input') {
      return picked.has(cell) ? 'mm__cell mm__cell--picked' : 'mm__cell'
    }
    if (review?.hit.includes(cell)) return 'mm__cell mm__cell--hit'
    if (review?.missed.includes(cell)) return 'mm__cell mm__cell--missed'
    if (review?.wrong.includes(cell)) return 'mm__cell mm__cell--wrong'
    return 'mm__cell'
  }

  const statusText =
    phase === 'show'
      ? t('game.memoryMatrix.hint')
      : phase === 'input'
        ? t('game.memoryMatrix.recall', { n: round.pattern.length })
        : t('game.memoryMatrix.review')

  return (
    <div className="mm">
      <div className="mm__top">
        <span className="mm__level">{t('game.level', { n: level })}</span>
        <span className="mm__count">
          {picked.size} / {round.pattern.length}
        </span>
      </div>

      <h2 className="mm__title">{t('game.memoryMatrix')}</h2>
      <p className="mm__status">{statusText}</p>

      <div className="mm__timer" aria-hidden="true">
        {phase === 'show' && (
          <div
            className="mm__timer-fill"
            style={{ animationDuration: `${round.showMs}ms` }}
          />
        )}
      </div>

      <div
        className="mm__grid"
        style={{ gridTemplateColumns: `repeat(${round.gridSize}, 1fr)` }}
        role="group"
        aria-label={t('game.memoryMatrix')}
      >
        {cells.map((cell) => (
          <button
            key={cell}
            type="button"
            className={cellClass(cell)}
            onClick={() => toggle(cell)}
            disabled={phase !== 'input'}
            aria-pressed={picked.has(cell)}
            aria-label={`${cell + 1}`}
          />
        ))}
      </div>

      <button
        type="button"
        className="mm__confirm"
        onClick={confirm}
        disabled={phase !== 'input' || picked.size === 0}
      >
        {t('game.done')}
      </button>
    </div>
  )
}
